import React from 'react';
import { CalendarIcon, ArrowCircleLeftIcon, ArrowCircleRightIcon } from '../icons';
import { colors } from '../../theme/tokens/colors';
import './DateSubheader.css';

export type PillColor = 'red' | 'green' | 'blue' | 'yellow' | 'orange' | 'grey';

export interface DateSubheaderProps {
    /** The date string to display (e.g., "Wed Sep 17") */
    dateLabel: string;
    /** Pill text (e.g., "Today", "Tomorrow") */
    pillLabel?: string;
    /** Pill color variant */
    pillColor?: PillColor;
    /** Callback when left arrow is clicked */
    onPrevious?: () => void;
    /** Callback when right arrow is clicked */
    onNext?: () => void;
    /** Callback when date area is clicked */
    onDateClick?: () => void;
    /** Optional icon to show on the right side */
    rightIcon?: React.ReactNode;
    /** Whether to show the right icon (defaults to false) */
    showRightIcon?: boolean;
    /** Optional time label to show on the right side (e.g., "12:00 PM") */
    timeLabel?: string;
    /** Whether to show the navigation arrows (defaults to true) */
    showNavArrows?: boolean;
    /** Whether the date is clickable (defaults to true) */
    dateClickable?: boolean;
    /** Optional className for the wrapper */
    className?: string;
}

const pillColorMap: Record<PillColor, { background: string; text: string }> = {
    red: { background: colors.accentRed, text: colors.white },
    green: { background: colors.green2, text: colors.white },
    blue: { background: colors.accentBlue, text: colors.white },
    yellow: { background: colors.accentYellow, text: colors.black },
    orange: { background: colors.orange2, text: colors.black },
    grey: { background: '#404040', text: colors.white },
};

export const DateSubheader: React.FC<DateSubheaderProps> = ({
    dateLabel,
    pillLabel,
    pillColor = 'red',
    onPrevious,
    onNext,
    onDateClick,
    rightIcon,
    showRightIcon = false,
    timeLabel,
    showNavArrows = true,
    dateClickable = true,
    className = '',
}) => {
    const pillStyles = pillColorMap[pillColor];

    return (
        <div className={`date-subheader ${className}`}>
            {/* Left section: Pill */}
            <div className="date-subheader__left">
                {pillLabel && (
                    <span
                        className="date-subheader__pill"
                        style={{
                            backgroundColor: pillStyles.background,
                            color: pillStyles.text,
                        }}
                    >
                        {pillLabel}
                    </span>
                )}
            </div>

            {/* Center section: Date picker */}
            <div className="date-subheader__center">
                {showNavArrows && (
                    <button
                        className="date-subheader__nav-button"
                        onClick={onPrevious}
                        aria-label="Previous day"
                    >
                        <ArrowCircleLeftIcon size="m" tone="lightGrey" />
                    </button>
                )}

                <div
                    className={`date-subheader__date ${dateClickable ? 'date-subheader__date--clickable' : ''}`}
                    onClick={dateClickable ? onDateClick : undefined}
                    role={dateClickable ? 'button' : undefined}
                    tabIndex={dateClickable ? 0 : undefined}
                >
                    <CalendarIcon size="s" tone="white" style={{ width: 14, height: 14 }} />
                    <span className="date-subheader__date-text">{dateLabel}</span>
                </div>

                {showNavArrows && (
                    <button
                        className="date-subheader__nav-button"
                        onClick={onNext}
                        aria-label="Next day"
                    >
                        <ArrowCircleRightIcon size="m" tone="lightGrey" />
                    </button>
                )}
            </div>

            {/* Right section: Time label or optional icon */}
            <div className="date-subheader__right">
                {timeLabel && (
                    <span className="date-subheader__time">{timeLabel}</span>
                )}
                {showRightIcon && rightIcon && (
                    <button className="date-subheader__icon-button">
                        {rightIcon}
                    </button>
                )}
            </div>
        </div>
    );
};

export default DateSubheader;
